import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { haptic } from '../../hooks/useTelegram';
import { copyText } from '../../lib/clipboard';
import { fmtUsd } from '../../lib/format';
import type { CardChallenge } from '../../lib/cards';
import { FigIcon } from '../FigIcon';
import { Sheet } from './Sheet';

/**
 * Banana CARD_3DS / CARD_VERIFICATION prompt.
 *
 * - OTP / wallet verification: show the code; the user types it on the merchant
 *   or Apple/Google Pay page. Nothing to confirm via the provider API.
 * - OOB payment: Approve / Not now — those call `/card/confirm_3ds`.
 */
export function CardChallengeSheet({
  challenge,
  busy,
  onApprove,
  onDismiss,
}: {
  challenge: CardChallenge;
  busy: boolean;
  onApprove: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const amount = challenge.amount ? Number(challenge.amount) : null;
  const method = (challenge.method ?? '').toUpperCase();
  const isWallet = challenge.kind === 'wallet';
  // Docs: OTP (and wallet verification) is relay-only; OOB is confirmable.
  const isCodeRelay =
    isWallet || method === 'OTP' || method === 'VERIFICATION' || (!!challenge.otp && method !== 'OOB');

  return (
    <Sheet
      onClose={onDismiss}
      panelClassName="flex w-full flex-col items-center gap-6 rounded-t-[36px] bg-surface px-4 pb-[44px] pt-6"
    >
      <span className="grid size-[72px] place-items-center rounded-full bg-accent/10 text-accent">
        <FigIcon name={isWallet ? 'pfCard' : 'pfLock'} size={34} />
      </span>

      <div className="flex flex-col items-center gap-2 text-center">
        <h2 className="text-[20px] font-medium text-ink">
          {isWallet ? t('cards.challengeWalletTitle') : t('cards.challengePaymentTitle')}
        </h2>
        <p className="max-w-[300px] text-[14px] leading-[1.44] text-muted">
          {isWallet
            ? t('cards.challengeWalletDesc')
            : isCodeRelay
              ? t('cards.challengeOtpDesc')
              : amount != null && Number.isFinite(amount)
                ? t('cards.challengePaymentAmountDesc', { amount: fmtUsd(amount) })
                : t('cards.challengePaymentDesc')}
        </p>
      </div>

      {challenge.otp && (
        <button
          onClick={async () => {
            haptic();
            await copyText(challenge.otp!);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="flex w-full flex-col items-center gap-2 rounded-[26px] bg-card px-4 py-5 active:opacity-80"
        >
          <span className="text-[13px] font-medium uppercase tracking-[0.08em] text-faint">
            {t('cards.challengeCode')}
          </span>
          <span className="text-[34px] font-bold leading-none tracking-[0.12em] tabular-nums text-ink">
            {challenge.otp}
          </span>
          <span className="text-[13px] text-accent">
            {copied ? t('common.copied') : t('common.copy')}
          </span>
        </button>
      )}

      {isCodeRelay ? (
        <button
          onClick={() => {
            if (busy) return;
            haptic('medium');
            onDismiss();
          }}
          disabled={busy}
          className="flex h-[56px] w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white transition-colors active:bg-accent-ink"
        >
          {busy ? (
            <span className="size-6 animate-spin rounded-full border-[3px] border-card/30 border-t-white" />
          ) : (
            t('cards.challengeGotIt')
          )}
        </button>
      ) : (
        <>
          <button
            onClick={() => {
              if (busy) return;
              haptic('medium');
              onApprove();
            }}
            disabled={busy}
            className="flex h-[56px] w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white transition-colors active:bg-accent-ink"
          >
            {busy ? (
              <span className="size-6 animate-spin rounded-full border-[3px] border-card/30 border-t-white" />
            ) : (
              t('cards.challengeApprove')
            )}
          </button>
          <button onClick={onDismiss} className="text-[15px] font-medium text-muted active:opacity-70">
            {t('cards.challengeLater')}
          </button>
        </>
      )}
    </Sheet>
  );
}
